import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { Tag } from '@zendeskgarden/react-tags';
import { isFacilitator } from 'utility/hasAccount';
import { canUseDOM } from 'exenv';
import { Redirect } from 'react-router';
import Spinner from 'components/Spinner/Spinner';
import moment from 'moment';
import { SM } from '@zendeskgarden/react-typography';
import Flex from 'styled-flex-component';
import { Avatar } from 'theme/Global.styles';
import { DraftWrapper } from 'components/MegadraftWrapper/MegadraftWrapper';
import { forEach, get, map } from 'lodash';
import { useBranch } from 'baobab-react/hooks';
import PageViewWrapper from 'higherOrder/PageViewWrapper';
import { getContent, getSimilarContent } from 'state/content/actions';
import { fetchPublicMeta } from 'state/application/actions';
import ShareSheet from 'components/ShareSheet/ShareSheet';
import { HelmetWrapper } from 'utility/DefaultMeta';
import { StickyContainer, Sticky } from 'react-sticky';
import isMobile from 'utility/isMobile';
import CustomBreadcrumbs from 'utility/CustomBreadcrumbs';
import { SectionHeader } from 'routes/Landing/Landing.styles';
import { Button } from 'theme/Button';
import history from 'historyContainer';
import RelatedBlogPosts from './RelatedBlogPosts';
import {
  BlogPostTemplatePage,
  BlogPostShare,
  BlogPage,
  RelatedBlogPostsWrapper,
  MobileWrapper,
  TagWrapper,
  GlobalWarehouseBottomBanner,
} from './BlogPostContent.styles';

function getMeta(contentDetails, location) {
  if (!contentDetails) {
    return null;
  }
  const { result } = contentDetails || {};
  const {
    preview,
    category_slug: categorySlug,
    category_name: categoryName,
    slug,
    id,
    published_date: publishedDate,
    update_timestamp: updated,
    writer_name: writerName,
  } = result || {};
  const title = get(preview, 'title');
  const description = get(preview, 'description');
  const image = get(contentDetails, 'result.images[0].image');
  const writer = writerName || 'Pipesearch';
  const previewImage = get(contentDetails, 'result.images[0].image');

  const properties = {
    title,
    description,
    image,
  };
  if (canUseDOM) {
    properties.url = window.location.href;
  } else if (location) {
    properties.url = location.pathname;
  } else {
    properties.url = '';
  }
  const meta = [];

  forEach(properties, (v, k) => {
    if (v) {
      meta.push({ property: `og:${k}`, content: v });
      meta.push({ name: `twitter:${k}`, content: v });
    }
  });
  meta.push({ name: 'description', content: description });
  meta.push({ name: 'robots', content: 'all' });

  const breadcrumbSchema = {
    '@context': 'http://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        item: {
          '@id': `https://www.pipesearch.com/${categorySlug}`,
          name: categoryName,
        },
      },
      {
        '@type': 'ListItem',
        position: 2,
        item: {
          '@id': `https://www.pipesearch.com/${categorySlug}/${slug}/${id}`,
          name: slug,
        },
      },
    ],
  };

  const json = {
    '@context': 'http://schema.org/',
    '@type': 'Blog',
    headline: title,
    image: previewImage,
    url: `https://www.pipesearch.com/${categorySlug}/${slug}/${id}`,
    datePublished: publishedDate,
    dateModified: updated,
    author: writer,
    description,
  };

  const script = [
    {
      type: 'application/ld+json',
      innerHTML: JSON.stringify([json, breadcrumbSchema], null, 2),
    },
  ];

  const link = [];
  return { title, meta, link, script };
}

function BlogPostContent({ match, location, route }) {
  const [clamp, setClamp] = useState(true);

  const [render, setRender] = useState(false);
  useEffect(() => {
    setRender(true);
  }, []);

  const contentId = get(match, 'params.id');
  const {
    similarContent,
    publicMeta,
    contentDetailsMap = {},
  } = useBranch({
    contentDetailsMap: ['content', 'details'],
    publicMeta: ['application', 'publicMeta'],
    similarContent: ['content', 'similar'],
  });
  const contentDetails = contentDetailsMap && contentDetailsMap[contentId];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [contentId]);

  useEffect(() => {
    if (contentId && (!contentDetails || !publicMeta)) {
      fetchPublicMeta();
      getContent(contentId);
    }
  }, [contentDetails, contentId, publicMeta]);

  useEffect(() => {
    if (contentDetails) {
      getSimilarContent(contentId, {
        pagination: { limit: 10 },
        sorting: { direction: 'desc' },
      });
    }
  }, [contentDetails, contentId]);
  const { result, loading } = contentDetails || {};
  const {
    slug: contentSlug,
    preview,
    tags: contentTags,
    category_name: categoryName,
    category_slug: categorySlug,
    published_date: publishedDate,
    company_name: company,
    co_company_name: coCompany,
    writer_name: writerName,
    co_writer_name: coWriter,
    button_name: buttonName,
    button_url: buttonUrl,
    products,
    listings,
    images,
    files,
    content,
  } = result || {};

  const routeSlug = match.params.slug;
  const slugsMatch = contentSlug && routeSlug === contentSlug;

  const previewTitle = get(preview, 'title');
  const previewDesc = get(preview, 'description');
  const previewImage = get(result, 'images[0].image');

  const metadata = getMeta(contentDetails, location);

  const StickyWrapper = isMobile() ? MobileWrapper : StickyContainer;
  const similarContentLength = get(similarContent, 'result.docs.length');
  const shareScrollBottomOffset = clamp
    ? 1860
    : 1860 + (similarContentLength / 4 || 1) * 320;

  return (
    <BlogPage>
      <StickyWrapper>
        <div style={{ minHeight: '400px' }}>
          {!slugsMatch && canUseDOM ? (
            <Redirect
              from={`/blog/${routeSlug}/${contentId}`}
              to={`/blog/${contentSlug}/${contentId}`}
            />
          ) : null}

          <HelmetWrapper metadata={metadata} />

          <BlogPostTemplatePage
            style={{
              paddingTop: '30px',
              overflow: `hidden`,
              position: 'relative',
            }}
          >
            <div
              style={{
                width: '100%',
                maxWidth: '790px',
                padding: '0 20px',
                margin: '0px auto 30px',
              }}
            >
              <CustomBreadcrumbs
                routes={route}
                origin={{ link: '/blog', title: 'Resources' }}
              />
            </div>

            <Flex
              alignCenter
              style={{
                width: '100%',
                maxWidth: '790px',
                padding: '0 20px',
                margin: '0px auto 0',
              }}
            >
              <Avatar
                scale="45px"
                medium
                position="center center"
                size="contain"
                noRadius
                src="/images/logo-blue-pipe.svg"
              />
              <div style={{ marginLeft: '13px' }}>
                <SM
                  tag="h6"
                  style={{
                    lineHeight: '28px',
                    marginBottom: '4px',
                    fontSize: '16px',
                  }}
                  bold
                >
                  {writerName || 'Pipesearch'}{' '}
                  {publishedDate ? (
                    <span>
                      {' '}
                      <em>{company ? `, ${company} ` : ''}| </em>
                      {buttonName ? (
                        <Tag
                          button
                          pill
                          style={{
                            fontSize: '11px',
                            marginLeft: '10px',
                            marginRight: '5px',
                            height: '22px',
                          }}
                        >
                          <a href={buttonUrl}> {buttonName}</a>
                        </Tag>
                      ) : (
                        ''
                      )}
                      {coWriter ? (
                        <div style={{ fontWeight: 'normal' }}>
                          {`${coWriter}, `}
                          <em>{`${coCompany}`}</em>
                        </div>
                      ) : (
                        ' '
                      )}
                      <span style={{ fontSize: '14px' }} className="slate">
                        {moment(publishedDate).fromNow()}
                      </span>
                    </span>
                  ) : (
                    ''
                  )}
                </SM>
                {categorySlug && categoryName ? (
                  <Link to={`/${categorySlug}`}>
                    <SM
                      tag="span"
                      style={{
                        lineHeight: '14px',
                        fontSize: '14px',
                        marginBottom: '4px',
                      }}
                      slate
                    >
                      {categoryName || ''}
                    </SM>
                  </Link>
                ) : null}
              </div>
            </Flex>

            {contentTags && contentTags.length ? (
              <TagWrapper
                style={{
                  width: '100%',
                  maxWidth: '790px',
                  padding: '10px 33px 0',
                  margin: '0px auto 25px',
                }}
              >
                {map(contentTags, (t, i) => (
                  <Tag
                    key={i}
                    button
                    pill
                    style={{
                      fontSize: '11px',
                      marginRight: '5px',
                      height: '22px',
                    }}
                  >
                    <Link to={`/tags/${t.slug}`}>{t.name}</Link>
                  </Tag>
                ))}
              </TagWrapper>
            ) : null}

            {previewTitle && isFacilitator() && publicMeta && render ? (
              <BlogPostShare className="mobile">
                <ShareSheet
                  title={previewTitle}
                  image={previewImage}
                  summary={previewDesc}
                  source="PipeSearch"
                />
              </BlogPostShare>
            ) : null}

            <div style={{ minHeight: '600px', marginBottom: '0' }}>
              {publicMeta ? (
                <DraftWrapper
                  publicPage
                  readOnly
                  contentId={Number(contentId)}
                  loading={loading}
                  editorState={content}
                  editorOpen
                  pluginProps={{
                    contentType: get(publicMeta, 'image_content_types.Content'),
                    objectId: Number(contentId),
                    products,
                    listings,
                    images,
                    files,
                    tags: contentTags,
                  }}
                />
              ) : (
                <Spinner />
              )}
            </div>
          </BlogPostTemplatePage>

          {similarContentLength ? (
            <div
              style={{
                backgroundColor: '#fff',
                position: 'relative',
                marginTop: '60px',
                paddingBottom: 10,
              }}
            >
              <RelatedBlogPostsWrapper>
                <div style={{ position: 'static' }}>
                  <RelatedBlogPosts
                    clamp={clamp}
                    setClamp={setClamp}
                    contentList={similarContent}
                  />
                </div>
              </RelatedBlogPostsWrapper>
            </div>
          ) : null}

          <GlobalWarehouseBottomBanner>
            <SectionHeader id="globalWarehouseBanner">
              Let&apos;s find the inventory to meet your need!
            </SectionHeader>
            <Button
              id="getStartedButton"
              size="large"
              primary
              bold
              onClick={() => {
                history.push('/global-warehouse');
              }}
            >
              Search the Global Warehouse
            </Button>
          </GlobalWarehouseBottomBanner>

          {previewTitle &&
          isFacilitator() &&
          publicMeta &&
          !isMobile() &&
          render ? (
            <Sticky disableCompensation bottomOffset={shareScrollBottomOffset}>
              {({ style }) => (
                <BlogPostShare className="desktop" style={style}>
                  <ShareSheet
                    title={previewTitle}
                    image={previewImage}
                    summary={previewDesc}
                    source="PipeSearch"
                  />
                </BlogPostShare>
              )}
            </Sticky>
          ) : null}
        </div>
      </StickyWrapper>
    </BlogPage>
  );
}

BlogPostContent.propTypes = {
  // LINT OVERRIDE #2
  // https://github.com/remix-run/react-router/issues/6177
  // External object
  // eslint-disable-next-line react/forbid-prop-types
  location: PropTypes.object,
  // LINT OVERRIDE #2
  // https://github.com/remix-run/react-router/issues/6177
  // External object
  // eslint-disable-next-line react/forbid-prop-types
  match: PropTypes.object,
  // LINT OVERRIDE #2
  // https://github.com/remix-run/react-router/issues/6177
  // External object
  // eslint-disable-next-line react/forbid-prop-types
  route: PropTypes.object,
};

export default PageViewWrapper(BlogPostContent, {
  pageName: 'BlogPostContent',
});
